import React from 'react'
import type { LucideIcon } from 'lucide-react'
import { Users, ClipboardList, Clock, Shield, Calendar, KeyRound } from 'lucide-react'
import { useSessionStore } from '../../stores/useSessionStore'
import { IdentityByEventPage } from './pages/IdentityByEventPage'
import { IdentityByClientPage } from './pages/IdentityByClientPage'
import { UserAccessByClientPage } from './pages/UserAccessByClientPage'
import { PunchClockExportPage } from './pages/PunchClockExportPage'
import { PunchClockErrorsPage } from './pages/PunchClockErrorsPage'
import { AbsenceLatePage } from './pages/AbsenceLatePage'
import { IdentityAuditLogPage } from './pages/IdentityAuditLogPage'
import { ShiftAuditLogPage } from './pages/ShiftAuditLogPage'
import { VisitLogPage } from './pages/VisitLogPage'

export type ReportCategory = 'Identity' | 'Punch Clock' | 'Audit Logs' | 'Visit'
export type ReportGateKey = 'identity' | 'punchClock' | 'admin' | 'visit'

export interface ReportDef {
  slug: string
  title: string
  description: string
  icon: LucideIcon
  iconColor: string
  category: ReportCategory
  gate: ReportGateKey
  Component: React.ComponentType
}

export const REPORT_CATEGORIES: ReportCategory[] = ['Identity', 'Punch Clock', 'Audit Logs', 'Visit']

export const reports: ReportDef[] = [
  {
    slug: 'identity-event',
    title: 'Identity Export by Event',
    description: 'Export identities provisioned to a specific event',
    icon: Users,
    iconColor: 'text-blue-600 bg-blue-50',
    category: 'Identity',
    gate: 'identity',
    Component: IdentityByEventPage,
  },
  {
    slug: 'identity-client',
    title: 'Identity Export by Client',
    description: 'Export all identities for a client organization',
    icon: ClipboardList,
    iconColor: 'text-violet-600 bg-violet-50',
    category: 'Identity',
    gate: 'identity',
    Component: IdentityByClientPage,
  },
  {
    slug: 'user-access-client',
    title: 'User Access by Client',
    description: 'EMS access levels and external system access for a client (admin only)',
    icon: KeyRound,
    iconColor: 'text-indigo-600 bg-indigo-50',
    category: 'Identity',
    gate: 'admin',
    Component: UserAccessByClientPage,
  },
  {
    slug: 'punch-export',
    title: 'Punch Clock Export',
    description: 'Export punch clock data for payroll',
    icon: Clock,
    iconColor: 'text-emerald-600 bg-emerald-50',
    category: 'Punch Clock',
    gate: 'punchClock',
    Component: PunchClockExportPage,
  },
  {
    slug: 'punch-errors',
    title: 'Punch Clock Entries with Notes/Errors',
    description: 'Review punch clock entries that have notes or errors',
    icon: Clock,
    iconColor: 'text-amber-600 bg-amber-50',
    category: 'Punch Clock',
    gate: 'punchClock',
    Component: PunchClockErrorsPage,
  },
  {
    slug: 'absence-late',
    title: 'Absence & Late Report',
    description: 'Track absences and lates by staff name',
    icon: ClipboardList,
    iconColor: 'text-red-600 bg-red-50',
    category: 'Punch Clock',
    gate: 'punchClock',
    Component: AbsenceLatePage,
  },
  {
    slug: 'audit-log',
    title: 'Identity Audit Log',
    description: 'View changes made to identities (admin only)',
    icon: Shield,
    iconColor: 'text-red-600 bg-red-50',
    category: 'Audit Logs',
    gate: 'admin',
    Component: IdentityAuditLogPage,
  },
  {
    slug: 'shift-audit-log',
    title: 'Shift & Punch Audit Log',
    description: 'Track shift changes, assignments, and manual punch edits (admin only)',
    icon: Shield,
    iconColor: 'text-orange-600 bg-orange-50',
    category: 'Audit Logs',
    gate: 'admin',
    Component: ShiftAuditLogPage,
  },
  {
    slug: 'visit-log',
    title: 'Visit Log',
    description: 'View visitor check-in records',
    icon: Calendar,
    iconColor: 'text-teal-600 bg-teal-50',
    category: 'Visit',
    gate: 'visit',
    Component: VisitLogPage,
  },
]

// Per-report visibility — mirrors backend gates so users only see reports they can run
export function useReportGates(): Record<ReportGateKey, boolean> {
  const {
    hasAdmin,
    hasIdentityManagerForAnyone,
    hasPunchClockAccessForAnyone,
    hasGatekeeperAccessForAnyone,
  } = useSessionStore()
  const admin = hasAdmin()
  return {
    identity: admin || hasIdentityManagerForAnyone(),
    punchClock: admin || hasPunchClockAccessForAnyone(),
    admin,
    visit: admin || hasGatekeeperAccessForAnyone(),
  }
}
